import React, { useEffect, useState, useCallback } from 'react';
import { getIncidents, getIncident, addIncidentComment } from '../../utils/api';
import { formatShortTimestamp, getRoomDisplayMeta } from '../../utils';

const ESTADO_CFG = {
  abierto:     { label: 'Abierto',     cls: 'incident-estado-abierto',  icon: '🔴' },
  en_progreso: { label: 'En Progreso', cls: 'incident-estado-progreso', icon: '🟡' },
  atendido:    { label: 'Atendido',    cls: 'incident-estado-atendido', icon: '✅' },
};

const FILTER_TABS = [
  { key: 'all',         label: 'Todos' },
  { key: 'abierto',     label: 'Abiertos' },
  { key: 'en_progreso', label: 'En Progreso' },
  { key: 'atendido',    label: 'Atendidos' },
];

const VALID_STATES = ['abierto', 'en_progreso', 'atendido'];

/* ── Incident summary row ── */
function IncidentRow({ incident, isSelected, onClick }) {
  const cfg = ESTADO_CFG[incident.estado] || ESTADO_CFG.abierto;
  const displayName = getRoomDisplayMeta(incident.room_code).displayName;
  return (
    <button
      type="button"
      className={`ilog-row ${isSelected ? 'selected' : ''} ${incident.level}`}
      onClick={onClick}
    >
      <div className="ilog-row-top">
        <span className={`ilog-level-dot ${incident.level}`} />
        <span className="ilog-room">{displayName}</span>
        <span className={`incident-estado-chip ${cfg.cls}`}>{cfg.icon} {cfg.label}</span>
      </div>
      <div className="ilog-sensor">{incident.sensor_label}</div>
      <div className="ilog-row-meta">
        <span>{incident.value_label}</span>
        <span className="ilog-sep">·</span>
        <span>{incident.created_by_name}</span>
        <span className="ilog-sep">·</span>
        <span>{formatShortTimestamp(incident.created_at)}</span>
      </div>
    </button>
  );
}

/* ── Comment bubble (reused from IncidentPanel) ── */
function CommentBubble({ comment }) {
  const estadoCfg = comment.estado_cambio ? ESTADO_CFG[comment.estado_cambio] : null;
  return (
    <div className="incident-comment">
      <div className="incident-comment-meta">
        <span className="incident-comment-author">{comment.usuario_nombre}</span>
        <span className="incident-comment-time">{formatShortTimestamp(comment.created_at)}</span>
        {estadoCfg && (
          <span className={`incident-estado-chip ${estadoCfg.cls}`}>
            {estadoCfg.icon} {estadoCfg.label}
          </span>
        )}
      </div>
      <p className="incident-comment-body">{comment.contenido}</p>
    </div>
  );
}

/* ── Detail pane ── */
function IncidentDetail({ incidentId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [pendingStatus, setPendingStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getIncident(incidentId);
      setData({ ...res.incident, comments: res.comments });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [incidentId]);

  useEffect(() => { load(); }, [load]);

  const handleAddComment = async () => {
    if (!commentText.trim() || !data) return;
    setSaving(true);
    setError(null);
    try {
      await addIncidentComment(data.id, { contenido: commentText.trim(), nuevo_estado: pendingStatus });
      setCommentText('');
      setPendingStatus(null);
      await load();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div className="ilog-detail-loading">
      <div className="incident-spinner" />
      <span>Cargando…</span>
    </div>
  );
  if (!data) return null;

  const cfg = ESTADO_CFG[data.estado] || ESTADO_CFG.abierto;
  const displayName = getRoomDisplayMeta(data.room_code).displayName;

  return (
    <div className="ilog-detail">
      <div className="ilog-detail-header">
        <button className="ilog-back-btn" onClick={onBack}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Volver a la lista
        </button>

        <div className="ilog-detail-title-row">
          <div>
            <div className="ilog-detail-room">{displayName}</div>
            <div className="ilog-detail-sensor">{data.sensor_label}</div>
          </div>
          <span className={`incident-estado-chip ${cfg.cls}`}>{cfg.icon} {cfg.label}</span>
        </div>

        <div className="ilog-detail-meta">
          <span>{data.value_label}</span>
          <span className="ilog-sep">·</span>
          <span>{data.threshold_detail}</span>
        </div>
        <div className="ilog-detail-meta" style={{marginTop:'0.2rem'}}>
          <span>Abierto por <strong>{data.created_by_name}</strong></span>
          <span className="ilog-sep">·</span>
          <span>{formatShortTimestamp(data.created_at)}</span>
        </div>
      </div>

      <div className="ilog-detail-body">
        <div className="incident-section-label" style={{marginBottom:'0.6rem'}}>
          Bitácora
          <span className="incident-comments-count">{data.comments?.length || 0}</span>
        </div>

        <div className="incident-comments-list" style={{marginBottom:'1rem'}}>
          {data.comments?.map(c => <CommentBubble key={c.id} comment={c} />)}
        </div>

        {data.estado !== 'atendido' && (
          <div className="incident-add-comment">
            <div className="incident-section-label">Agregar actualización</div>
            <div className="incident-status-btns">
              {VALID_STATES.filter(s => s !== data.estado).map(s => (
                <button
                  key={s}
                  className={`incident-status-btn ${pendingStatus === s ? 'selected' : ''} ${s}`}
                  onClick={() => setPendingStatus(p => p === s ? null : s)}
                >
                  {ESTADO_CFG[s].icon} Cambiar a {ESTADO_CFG[s].label}
                </button>
              ))}
            </div>
            <div className="incident-textarea-wrap">
              <textarea
                className="incident-textarea"
                placeholder="Escribe una actualización…"
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                rows={3}
                maxLength={1000}
              />
              <div className="incident-textarea-count">{commentText.length}/1000</div>
            </div>
            {error && <div className="incident-error">{error}</div>}
            <button
              className={`incident-btn-primary ${pendingStatus === 'atendido' ? 'success' : ''}`}
              onClick={handleAddComment}
              disabled={saving || !commentText.trim()}
            >
              {saving
                ? <><div className="incident-btn-spinner"/> Guardando…</>
                : pendingStatus === 'atendido'
                  ? '✓ Marcar como Atendido'
                  : pendingStatus
                    ? `Enviar y cambiar a ${ESTADO_CFG[pendingStatus].label}`
                    : 'Enviar actualización'}
            </button>
          </div>
        )}

        {data.estado === 'atendido' && (
          <div className="incident-resolved-banner">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
            </svg>
            <div>
              <strong>Incidente atendido</strong>
              <span>por {data.resuelto_by_name} · {formatShortTimestamp(data.resuelto_at)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Main panel ── */
export function IncidentsLogPanel({ isOpen, onClose }) {
  const [filter, setFilter] = useState('all');
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? { estado: filter } : {};
      const res = await getIncidents(params);
      setIncidents(res.incidents || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => {
    if (isOpen) { setSelectedId(null); load(); }
  }, [isOpen, load]);

  if (!isOpen) return null;

  const counts = {
    all:         incidents.length,
    abierto:     incidents.filter(i => i.estado === 'abierto').length,
    en_progreso: incidents.filter(i => i.estado === 'en_progreso').length,
    atendido:    incidents.filter(i => i.estado === 'atendido').length,
  };

  const visible = filter === 'all' ? incidents : incidents.filter(i => i.estado === filter);

  return (
    <>
      <div className="ilog-overlay" onClick={onClose} />
      <aside className="ilog-panel">

        {/* Header */}
        <div className="ilog-header">
          <div className="ilog-header-top">
            <div className="ilog-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l3 3L22 4"/>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
              </svg>
              Seguimiento de Alertas
            </div>
            <button className="incident-panel-close" onClick={onClose} aria-label="Cerrar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M18 6 6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>

          {/* Filter tabs */}
          {!selectedId && (
            <div className="ilog-tabs">
              {FILTER_TABS.map(tab => (
                <button
                  key={tab.key}
                  className={`ilog-tab ${filter === tab.key ? 'active' : ''}`}
                  onClick={() => { setFilter(tab.key); setSelectedId(null); }}
                >
                  {tab.label}
                  {counts[tab.key] > 0 && (
                    <span className="ilog-tab-count">{counts[tab.key]}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="ilog-body">
          {selectedId ? (
            <IncidentDetail
              incidentId={selectedId}
              onBack={() => { setSelectedId(null); load(); }}
            />
          ) : loading ? (
            <div className="ilog-empty">
              <div className="incident-spinner" />
              <span>Cargando seguimientos…</span>
            </div>
          ) : visible.length === 0 ? (
            <div className="ilog-empty">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
              </svg>
              <p>Sin seguimientos {filter !== 'all' ? `con estado "${ESTADO_CFG[filter]?.label}"` : 'registrados'}</p>
            </div>
          ) : (
            <div className="ilog-list">
              {visible.map(inc => (
                <IncidentRow
                  key={inc.id}
                  incident={inc}
                  isSelected={selectedId === inc.id}
                  onClick={() => setSelectedId(inc.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {!selectedId && visible.length > 0 && (
          <div className="ilog-footer">
            <span>{visible.length} seguimiento{visible.length !== 1 ? 's' : ''}</span>
            <button className="ilog-refresh-btn" onClick={load} disabled={loading}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                <path d="M21 3v5h-5"/>
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                <path d="M3 21v-5h5"/>
              </svg>
              Actualizar
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
